import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HiddenTruthsPanel from "@/components/HiddenTruthsPanel";
import { ArrowLeft, ExternalLink, Bookmark, BookmarkCheck, Loader2, Flame, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function StoryDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${id}`)
      .then((r) => setStory(r.data))
      .catch(() => toast.error("Story not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookmarks/ids").then((r) => setIsBookmarked(r.data.ids.includes(id))).catch(() => {});
  }, [id, user]);

  const toggleBookmark = async () => {
    if (!user) { toast.error("Sign in to save"); nav("/login"); return; }
    try {
      if (isBookmarked) {
        await api.delete(`/bookmarks/${id}`);
        setIsBookmarked(false);
        toast.success("Removed from bookmarks");
      } else {
        await api.post("/bookmarks", { story_id: id });
        setIsBookmarked(true);
        toast.success("Saved");
      }
    } catch { toast.error("Action failed"); }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading dossier…
      </div>
    );
  }

  if (!story) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500">Story not found.</p>
        <Link to="/" className="font-mono-head text-xs uppercase tracking-widest text-[#E60000] mt-4 inline-block">← Back to Feed</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/" data-testid="back-to-feed" className="inline-flex items-center gap-1 font-mono-head text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 mb-8">
        <ArrowLeft className="w-3 h-3" /> Back to feed
      </Link>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center border border-stone-300 px-2 py-1 text-[10px] font-mono-head uppercase tracking-widest text-stone-700 bg-stone-100">
          {story.source}
        </span>
        <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-900">· {story.category}</span>
        <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-400">
          · {String(story.published).slice(0, 25)}
        </span>
        <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-[#E60000]">
          <Flame className="w-3 h-3" /> Heat {story.heat_score}
        </span>
      </div>

      <h1 className="font-serif-head text-4xl sm:text-5xl text-stone-900 leading-[1.05] mb-6">
        {story.title}
      </h1>

      <p className="text-lg leading-relaxed text-stone-700 mb-8 border-l-4 border-stone-900 pl-5">
        {story.summary}
      </p>

      <div className="flex flex-wrap gap-3 mb-10">
        <a
          href={story.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="detail-source-link"
          className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 font-mono-head text-xs uppercase tracking-widest px-5 py-3 hover:bg-[#E60000] transition-colors"
        >
          Read at {story.source} <ExternalLink className="w-3 h-3" />
        </a>
        <button
          data-testid="detail-bookmark-btn"
          onClick={toggleBookmark}
          className="inline-flex items-center gap-2 border border-stone-900 font-mono-head text-xs uppercase tracking-widest px-5 py-3 hover:bg-stone-900 hover:text-stone-50 transition-colors"
        >
          {isBookmarked ? <BookmarkCheck className="w-4 h-4 text-[#E60000]" /> : <Bookmark className="w-4 h-4" />}
          {isBookmarked ? "Saved" : "Save to Archive"}
        </button>
        <button
          data-testid="detail-share-btn"
          onClick={async () => {
            const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/story/${story.id}`;
            try {
              if (navigator.share) {
                await navigator.share({ title: story.title, text: story.summary, url: shareUrl });
              } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Share link copied — preview includes OG image");
              }
            } catch { /* user cancelled */ }
          }}
          className="inline-flex items-center gap-2 border border-stone-300 font-mono-head text-xs uppercase tracking-widest px-5 py-3 hover:border-[#E60000] hover:text-[#E60000] transition-colors"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>

      <HiddenTruthsPanel storyId={story.id} />
    </div>
  );
}
